import {AfterViewInit, booleanAttribute, ChangeDetectionStrategy, Component, DestroyRef, ElementRef, Inject, InjectionToken, Input, ViewChild} from '@angular/core';
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {MedlemsRolleModell, TilgangsrolleMedlem} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {AbstractControl, FormControl, FormGroup, FormRecord, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators} from "@angular/forms";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatInputModule} from "@angular/material/input";
import {MatRadioModule} from "@angular/material/radio";
import {MatIconModule} from "@angular/material/icon";
import {MatDatepickerModule} from "@angular/material/datepicker";
import {MatOptionModule} from "@angular/material/core";
import {AsyncPipe} from "@angular/common";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {Observable} from "rxjs";
import {nyUuid} from "felles-ng2/støttefunksjoner/uuidGenerator";
import {MatSelectModule} from "@angular/material/select";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {TranslateModule} from "@ngx-translate/core";
import {FeltValidatorer} from "felles-ng2/infrastruktur/angular/feltValidatorer";
import {MobilnummerFeltKomponent} from "web-ng2/delt/komponenter/mobilnummer-felt/mobilnummer-felt";
import {StandardValideringsfeilPipe} from "felles-ng2/pipes/StandardValideringsfeilPipe";
import {MatCheckbox} from "@angular/material/checkbox";
import {muligeTilgangsrollerForMedlem, TilgangsrollerMedlemInfo, ValiderEpostRespons} from "felles-ng2/modell/medlemModellHjelpere";
import {EnumOversettelser} from "felles-ng2/tjenester/i18n/EnumOversettelser";
import {enumInnslag, navnPåAlleEnuminnslag, navnPåEnuminnslag} from "felles-ng2/støttefunksjoner/enumer";
import {erIkkeTomStreng, erTomEllerNil} from "felles-ng2/støttefunksjoner/strenger";
import {Temporal} from "temporal-polyfill";
import PlainDate = Temporal.PlainDate;

const debug = initDebugger('MedlemInvitasjonSkjemaKomponent');

/**  InjectionToken for injisering av {@link MedlemInvitasjonSkjemaKomponentAvhengigheter} implementasjon. */
export const MedlemInvitasjonSkjemaKomponentAvhengigheter_DI_TOKEN = new InjectionToken<MedlemInvitasjonSkjemaKomponentAvhengigheter>('MedlemInvitasjonSkjemaKomponentAvhengigheter implementasjon');

export type SkjemaType = ReturnType<typeof MedlemInvitasjonSkjemaKomponent.opprettSkjema>;
export type SkjemaVerdier = SkjemaType['value'];

@Component({
    selector: 'medlem-invitasjon-skjema',
    templateUrl: './medlem-invitasjon-skjema.html',
    styleUrl: './medlem-invitasjon-skjema.scss',
    host: {'data-testid': 'medlem-registrer-skjema'},
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatRadioModule,
        MatIconModule,
        MatDatepickerModule,
        MatCheckbox,
        MatOptionModule,
        MatSelectModule,
        AsyncPipe,
        TranslateModule,
        MobilnummerFeltKomponent,
        StandardValideringsfeilPipe
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MedlemInvitasjonSkjemaKomponent implements AfterViewInit {

    static opprettSkjema() {
        const tilgangsroller = new FormRecord<FormControl<boolean>>({});
        muligeTilgangsrollerForMedlem().forEach(rolleinfo =>
          tilgangsroller.addControl(rolleinfo.enumNavn, new FormControl(rolleinfo.obligatorisk, {nonNullable: true})));

        return new FormGroup({
            id: new FormControl(nyUuid(), {nonNullable: true}), //ikke eksponert for redigering
            navn: new FormControl<string | null>(null, {validators: FeltValidatorer.litenStreng}),
            epost: new FormControl('', {nonNullable: true, validators: [Validators.required, ...FeltValidatorer.epost]}),
            mobilnummer: new FormControl<string | null>(null, {validators: FeltValidatorer.mobilnummer}),
            rolle: new FormControl<MedlemsRolleModell | null>(null, {validators: Validators.required}),
            rolleStartdato: new FormControl<PlainDate | null>(null, {validators: Validators.required}),
            tilgangsroller: tilgangsroller
        });
    }

    static lesTilgangsroller(skjema: SkjemaType): TilgangsrolleMedlem[] {
        const verdier = skjema.controls.tilgangsroller.getRawValue();
        const valgteRoller = navnPåAlleEnuminnslag(TilgangsrolleMedlem)
          .filter(rolenavn => verdier[rolenavn])
          .map(rolenavn => enumInnslag<TilgangsrolleMedlem>(rolenavn, TilgangsrolleMedlem))
        return valgteRoller;
    }

    static setTilgangsroller(skjema: SkjemaType, aktiverteRoller: TilgangsrolleMedlem[]): void {
        skjema.controls.tilgangsroller.reset();
        aktiverteRoller
          .map(rolle => navnPåEnuminnslag(rolle, TilgangsrolleMedlem))
          .forEach(rollenavn => skjema.controls.tilgangsroller.controls[rollenavn].setValue(true));
    }

    /** Referanse til epost-tekstfelt */
    @ViewChild('epostfelt') epostfelt!: ElementRef<HTMLInputElement>;

    roller!: Observable<MedlemsRolleModell[]>; //settes i ngAfterViewInit
    duplikatMedlemFinnes = false;
    tilgjengeligeTilgangsroller!: { rolleinfo: TilgangsrollerMedlemInfo, kontroller: FormControl<boolean> }[]; //settes i 'set skjema'

    private _skjema!: SkjemaType;
    private _fokuserEpostFelt = false;

    constructor(@Inject(MedlemInvitasjonSkjemaKomponentAvhengigheter_DI_TOKEN) private avhengigheter: MedlemInvitasjonSkjemaKomponentAvhengigheter,
                public enumOversettelser: EnumOversettelser,
                private destroyRef: DestroyRef) {
    }

    @Input({required: true}) set skjema(skjema: SkjemaType) {
        this._skjema = skjema;
        debug('skjema settes')

        //sjekk for duplikate navn, merk at vi ikke gjør dette via validators på skjemakontroll da vi tillater duplikate,
        //men ønsker å advare om slike
        this.skjema.controls.navn.valueChanges
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(navn => this.validerNavn(navn));

        //valider epost - vi tillater ikke duplikate
        this.skjema.controls.epost.addValidators(this.epostValidator());

        //init tilgjengeligeTilgangsroller, lytt til endringer
        this.tilgjengeligeTilgangsroller = muligeTilgangsrollerForMedlem()
          .map(rolleinfo => {
              const kontroller = this.skjema.controls.tilgangsroller.controls[rolleinfo.enumNavn];
              if (rolleinfo.obligatorisk) kontroller.disable();
              return {rolleinfo, kontroller};
          });

        this.skjema.controls.tilgangsroller.valueChanges
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(valgte => {
              //finn aktiverte roller som impliserer andre roller og aktiver disse
              this.tilgjengeligeTilgangsroller
                .filter(rolle => rolle.kontroller.value && erIkkeNil(rolle.rolleinfo.impliserer)) //kun aktiverte roller som impliserer andre roller
                .map(rolle => this.tilgjengeligeTilgangsroller.find(r => r.rolleinfo.rolle == rolle.rolleinfo.impliserer)!)
                .forEach(rolle => rolle.kontroller.setValue(true, {emitEvent: false}));
          });
    }

    get skjema() {
        return this._skjema;
    }

    @Input({transform: booleanAttribute}) set fokuserEpostFelt(fokuser: boolean) {
        debug('fokuserEpostFelt', fokuser)
        this._fokuserEpostFelt = fokuser;
        this.giEpostFeltFokus();
    }

    get fokuserEpostFelt() {
        return this._fokuserEpostFelt;
    }

    erValgtRolle(a: MedlemsRolleModell | null, b: MedlemsRolleModell | null) {
        return a?.id === b?.id;
    }

    ngAfterViewInit() {
        this.giEpostFeltFokus();
        this.roller = this.avhengigheter.medlemsroller();
    }

    //hjelpmetoder

    private giEpostFeltFokus() {
        debug('giEpostFeltFokus', this.fokuserEpostFelt)
        if (this.fokuserEpostFelt && erIkkeNil(this.epostfelt))
          //uten timeout rendrer ikke mat-form-field flytende label korrekt
            window.setTimeout(() => {
                this.epostfelt.nativeElement.focus()
            }, 200);
    }

    private validerNavn(navn: string | null) {
        this.duplikatMedlemFinnes = erIkkeTomStreng(navn) && this.avhengigheter.verifiserAtNavnIkkeAlleredeErRegistrert(navn);
    }

    private epostValidator(): ValidatorFn {
        return (epostKontroller: AbstractControl<string>): ValidationErrors | null => {
            //ikke kall ekstern validering før vi har en gyldig epost-adresse
            if (erTomEllerNil(epostKontroller.value) || !FeltValidatorer.gyldigEpost.test(epostKontroller.value)) return null;

            const resultat = this.avhengigheter.verifiserAtEpostIkkeAlleredeErRegistrert(epostKontroller.value);
            debug('epostValidator', epostKontroller.value, resultat)
            if (resultat.eksistererInaktivt || resultat.eksistererMedlem || resultat.eksistererInvitasjon)
                return resultat;
            return null;
        }
    }
}


export interface MedlemInvitasjonSkjemaKomponentAvhengigheter {

    /** False dersom navn allerede er registrert i den aktuelle gruppen */
    verifiserAtNavnIkkeAlleredeErRegistrert(navn: string): boolean;

    /** Kontrollerer hvorvidt angitt epost allerede er registrert i den aktuelle gruppen */
    verifiserAtEpostIkkeAlleredeErRegistrert(epost: string): ValiderEpostRespons;

    /** Returnerer tilgjengelige medlemsroller */
    medlemsroller(): Observable<MedlemsRolleModell[]>;
}
